import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import familyRepository from '../../services/repositories/family.repository';
import '../../styles/products.scss';

const FamilyIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [families, setFamilies] = useState([]);
  const [error, setError] = useState(null);

  const fetchFamilies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await familyRepository.getAll();
      
      const transformedFamilies = Array.isArray(response) ? response : (response.data || []);
      
      const formattedFamilies = transformedFamilies.map(family => ({
        id: family.id,
        name: family.name,
        description: family.description || '-',
      }));
      
      setFamilies(formattedFamilies);
    } catch (err) {
      console.error('Error fetching families:', err);
      setError(err.message || 'Error loading families');
      setFamilies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      header: 'Description',
      accessor: 'description',
      sortable: true,
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/families/family-add');
  }, [navigate]);

  const handleEdit = useCallback((family) => {
    navigate(`/admin/families/family-edit/${family.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (family) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${family.name}"?`)) {
      try {
        setLoading(true);
        await familyRepository.delete(family.id);
        alert('Famille supprimée avec succès');
        await fetchFamilies();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Erreur lors de la suppression de la famille');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchFamilies]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} familles?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await familyRepository.delete(id);
        }
        alert(`${selectedIds.length} familles supprimées avec succès`);
        await fetchFamilies();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Erreur lors de la suppression des familles');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchFamilies]);

  useEffect(() => {
    setTitle('Familles');
    fetchFamilies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="families-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={families}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title="Liste des Familles"
        addButtonText="Ajouter une Famille"
        searchPlaceholder="Rechercher des familles..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default FamilyIndex;
